// Durée pendant laquelle une erreur de code est ré-affichée après un re-rendu du tiroir.
const ERROR_RESTORE_DELAY = 8000;

if (!customElements.get("deployer-cart-discount")) {
  customElements.define(
    "deployer-cart-discount",
    class DeployerCartDiscount extends HTMLElement {
      static pendingError = null;

      constructor() {
        super();

        this.applyButton = this.querySelector(".deployer-cart-discount_button");
        this.input = this.querySelector(".deployer-cart-discount_input");
        this.removeButton = this.querySelector(".cart-discount__remove-discount");
        this.toggleButton = this.querySelector("[data-discount-toggle]");
        this.hideButton = this.querySelector("[data-discount-hide]");
        this.openPanel = this.querySelector("[data-discount-open]");
        this.field = this.querySelector("[data-discount-field]");
        this.errorElement = this.querySelector("[data-discount-error]");
        this.abortController = null;

        if (!this.applyButton || !this.input) {
          console.warn("[deployer-cart-discount] Bouton ou input introuvable.");
          return;
        }

        this.applyButton.addEventListener("click", this.submitDiscount.bind(this));
        // Le champ n'est pas dans un <form> : Entrée (ou « OK » du clavier mobile) valide le code.
        this.input.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" || event.isComposing) return;
          event.preventDefault();
          if (!this.applyButton.disabled) this.submitDiscount(event);
        });
        this.input.addEventListener("input", () => this.clearError());
        this.removeButton?.addEventListener("click", this.removeDiscount.bind(this));
        this.addEventListener("discount:remove", this.removeDiscount.bind(this));

        this.toggleButton?.addEventListener("click", () => {
          this.openPanel?.classList.add("show");
          this.toggleButton.style.display = "none";
          setTimeout(() => this.input.focus(), 200);
        });

        this.hideButton?.addEventListener("click", () => {
          this.clearError();
          this.openPanel?.classList.remove("show");
          if (this.toggleButton) this.toggleButton.style.display = "inline-flex";
        });

        this.hideButton?.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          this.hideButton.click();
        });

        this.syncLocalStorageWithCart();
        this.updateInputDisplay();
        this.restoreError();
      }

      async submitDiscount(event) {
        event.preventDefault();

        const discountCode = this.input.value.trim();
        this.clearError();

        try {
          const cart = await this.getCart();
          const activeDiscounts = this.getActiveDiscountCodes(cart);

          if (activeDiscounts.length > 0) {
            this.input.value = "";
            await this.removeDiscount();
            return;
          }

          if (!discountCode) {
            this.setErrorMode("Veuillez saisir un code");
            return;
          }

          await this.applyCode(discountCode);
        } catch (error) {
          console.error("Erreur lors de la soumission du code promo:", error);
          this.setErrorMode("Une erreur est survenue");
        }
      }

      async removeDiscount() {
        const badge = this.querySelector(".cart-discount_badge");

        if (badge?.classList.contains("loading")) return;

        this.setButtonLoading();
        badge?.classList.add("loading");

        try {
          await this.applyCode("");
        } catch (error) {
          console.error("Erreur lors de la suppression du code:", error);
          this.setErrorMode("Une erreur est survenue");
        } finally {
          this.setButtonLoaded();
          badge?.classList.remove("loading");
        }
      }

      notifyGiftDispatcher(code) {
        if (!code) return;
        document.dispatchEvent(
          new CustomEvent("gift:discount-manually-applied", {
            detail: { code: code.trim() },
          })
        );
      }

      async applyCode(discountCode) {
        this.setButtonLoading();

        if (this.abortController) {
          this.abortController.abort();
        }

        this.abortController = new AbortController();

        try {
          const cartData = await this.getCart(this.abortController.signal);
          const existingDiscounts = this.getActiveDiscountCodes(cartData);

          let finalDiscounts = [];

          if (discountCode) {
            // Déclencheur du cadeau (distinct de la remise) : on notifie AVANT la validation
            // Shopify, pour que le mot-clé déclenche le cadeau même si Shopify refuse le code.
            this.notifyGiftDispatcher(discountCode);

            const codeAlreadyApplied = existingDiscounts.some(
              (code) => code.toUpperCase() === discountCode.toUpperCase()
            );

            if (codeAlreadyApplied) {
              return;
            }

            finalDiscounts = [...existingDiscounts, discountCode];
          }

          const response = await fetch(window.Shopify.routes.root + "cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              discount: finalDiscounts.join(","),
              attributes: {
                discount: discountCode || null,
              },
              sections: window.deployerAdapter?.cartDrawerSection || "cart-drawer",
              sections_url: window.location.pathname,
            }),
            signal: this.abortController.signal,
          });

          if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status}`);
          }

          const updatedCart = await response.json();

          if (discountCode) {
            const failedDiscount = updatedCart.discount_codes?.find(
              (discount) =>
                discount.code?.toUpperCase() === discountCode.toUpperCase() &&
                !discount.applicable
            );

            if (failedDiscount) {
              this.removeStoredDiscount();
              this.setErrorMode("Ce code promo n'est pas applicable");
              await this.updateCartAttribute(null);
              return;
            }

            const appliedDiscount = updatedCart.discount_codes?.find(
              (discount) =>
                discount.code?.toUpperCase() === discountCode.toUpperCase() &&
                discount.applicable
            );

            if (!appliedDiscount) {
              this.removeStoredDiscount();
              this.setErrorMode("Ce code promo n'est pas valide");
              await this.updateCartAttribute(null);
              return;
            }

            this.storeDiscount(appliedDiscount.code.trim(), updatedCart.total_price || 0);
          } else {
            this.removeStoredDiscount();
          }

          this.updateInputDisplay();
          this.refreshCart(updatedCart);
        } catch (error) {
          if (error.name === "AbortError") return;

          console.error("Erreur applyCode:", error);
          this.setErrorMode("Une erreur est survenue");
        } finally {
          this.setButtonLoaded();
        }
      }

      async getCart(signal = null) {
        const response = await fetch(window.Shopify.routes.root + "cart.js", {
          signal,
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Erreur HTTP cart.js: ${response.status}`);
        }

        return await response.json();
      }

      getActiveDiscountCodes(cart) {
        return (
          cart.discount_codes
            ?.filter((discount) => discount.applicable)
            .map((discount) => discount.code)
            .filter(Boolean) || []
        );
      }

      async syncLocalStorageWithCart() {
        try {
          const cart = await this.getCart();
          const activeDiscounts = this.getActiveDiscountCodes(cart);

          if (activeDiscounts.length > 0) {
            this.storeDiscount(activeDiscounts[0], cart.total_price || 0);
          } else {
            this.removeStoredDiscount();
          }

          this.updateInputDisplay();
        } catch (error) {
          console.error("Erreur lors de la synchronisation du code promo:", error);
        }
      }

      async updateCartAttribute(discount = null) {
        try {
          const response = await fetch(window.Shopify.routes.root + "cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              attributes: {
                discount,
              },
              sections: window.deployerAdapter?.cartDrawerSection || "cart-drawer",
              sections_url: window.location.pathname,
            }),
          });

          if (!response.ok) {
            throw new Error(`Erreur HTTP updateCartAttribute: ${response.status}`);
          }

          const data = await response.json();
          this.refreshCart(data);
        } catch (error) {
          console.error("Erreur lors de la mise à jour des attributs du panier:", error);
        }
      }

      storeDiscount(code, totalCart = 0) {
        localStorage.setItem(
          "discountCode",
          JSON.stringify({
            code,
            totalCart,
          })
        );
      }

      removeStoredDiscount() {
        localStorage.removeItem("discountCode");
      }

      getStoredDiscount() {
        try {
          const storedDiscount = localStorage.getItem("discountCode");

          if (!storedDiscount) return null;

          return JSON.parse(storedDiscount);
        } catch (error) {
          console.warn("discountCode invalide dans localStorage. Suppression.");
          this.removeStoredDiscount();
          return null;
        }
      }

      updateInputDisplay() {
        const storedDiscount = this.getStoredDiscount();
        const hasDiscountCode = Boolean(storedDiscount?.code);
        const hasError = this.hasVisibleError();

        if (hasDiscountCode) {
          this.input.placeholder = "";
          this.input.value = storedDiscount.code;
        } else if (!hasError) {
          // Avec une erreur affichée, on garde la saisie pour que le client puisse la corriger.
          this.input.placeholder = this.input.dataset.defaultPlaceholder || "";
          this.input.value = "";
        }

        this.field?.classList.toggle("hidden", hasDiscountCode);
        this.querySelector(".cart-discount_badge-container")?.classList.toggle("hidden", !hasDiscountCode);

        if (hasDiscountCode || hasError) {
          this.openPanel?.classList.add("show");
          if (this.toggleButton) this.toggleButton.style.display = "none";
        } else {
          this.openPanel?.classList.remove("show");
          if (this.toggleButton) this.toggleButton.style.display = "inline-flex";
        }
      }

      setErrorMode(message = "Veuillez saisir un code valide") {
        this.setButtonLoaded();
        this.showError(message);
        this.input.focus({ preventScroll: true });
      }

      hasVisibleError() {
        return Boolean(this.errorElement && !this.errorElement.classList.contains("hidden"));
      }

      // Message sous le champ, qui reste affiché jusqu'à la prochaine action du client.
      showError(message, shownAt = Date.now()) {
        this.input.classList.add("deployer-cart-discount_input-error");
        this.input.setAttribute("aria-invalid", "true");

        if (this.errorElement) {
          // Vidé puis rempli après coup pour que role="alert" annonce bien le message.
          this.errorElement.textContent = "";
          this.errorElement.classList.remove("hidden");
          setTimeout(() => {
            if (this.hasVisibleError()) this.errorElement.textContent = message;
          }, 50);
          this.input.setAttribute("aria-describedby", this.errorElement.id);
        } else {
          this.input.placeholder = message;
          this.input.value = "";
        }

        // Le tiroir est re-rendu après un rafraîchissement du panier : on mémorise l'erreur
        // pour que la nouvelle instance de l'élément la ré-affiche.
        DeployerCartDiscount.pendingError = {
          message,
          code: this.input.value.trim(),
          at: shownAt,
        };
      }

      clearError() {
        DeployerCartDiscount.pendingError = null;
        this.input?.classList.remove("deployer-cart-discount_input-error");
        this.input?.removeAttribute("aria-invalid");
        this.input?.removeAttribute("aria-describedby");

        if (this.errorElement) {
          this.errorElement.textContent = "";
          this.errorElement.classList.add("hidden");
        }
      }

      restoreError() {
        const pending = DeployerCartDiscount.pendingError;
        if (!pending || !this.errorElement) return;

        const isRecent = Date.now() - pending.at < ERROR_RESTORE_DELAY;
        if (!isRecent || this.field?.classList.contains("hidden")) {
          DeployerCartDiscount.pendingError = null;
          return;
        }

        this.input.value = pending.code;
        this.showError(pending.message, pending.at);
        this.updateInputDisplay();
        this.input.focus({ preventScroll: true });
      }

      setButtonLoading() {
        this.applyButton.disabled = true;
        this.applyButton.classList.add("deployer-button--is-loading");
      }

      setButtonLoaded() {
        this.applyButton.disabled = false;
        this.applyButton.classList.remove("deployer-button--is-loading");
      }

      refreshCart(cartData = {}) {
        document.dispatchEvent(new CustomEvent("deployer-cart-drawer:refresh"));

        if (
          typeof publish === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "cart-drawer",
            cartData,
          });
        }

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cartData,
            },
          })
        );

        if (window.location.pathname.includes("/cart")) {
          window.location.reload();
        }
      }
    }
  );
}